import { apiService } from "@/lib/api/service";
import { sanitizeRedirectUrl } from "@/lib/url-safety";
import type { CommunityWorkflowsResponse } from "@/types/features/workflowTypes";

import type {
  CommunityIntegration,
  CommunityIntegrationsResponse,
  CreateCustomIntegrationRequest,
  CreateCustomIntegrationResponse,
  Integration,
  IntegrationInstructions,
  IntegrationToolsResponse,
  MyIntegrationsResponse,
  PublicIntegrationResponse,
} from "../types";

export interface IntegrationConfigResponse {
  integrations: Integration[];
}

export const integrationsApi = {
  /**
   * Get the configuration for all integrations from backend
   */
  getIntegrationConfig: async (): Promise<IntegrationConfigResponse> => {
    try {
      const response = (await apiService.get(
        "/integrations/config",
      )) as IntegrationConfigResponse;
      return response;
    } catch (error) {
      console.error("Failed to get integration config:", error);
      throw error;
    }
  },

  /**
   * Get the full catalog personalized for the user: every platform integration
   * plus the user's own custom ones, each annotated with connection status.
   * Per-tool schemas are not included — only `toolCount`. Fetch one
   * integration's tools on demand via `getIntegrationTools`.
   */
  getMyIntegrations: async (): Promise<MyIntegrationsResponse> => {
    return await apiService.get<MyIntegrationsResponse>("/integrations/me");
  },

  /**
   * Get the full tool list for a single integration, on demand.
   */
  getIntegrationTools: async (
    integrationId: string,
  ): Promise<IntegrationToolsResponse> => {
    return await apiService.get<IntegrationToolsResponse>(
      `/integrations/${integrationId}/tools`,
      { silent: true },
    );
  },

  /**
   * Get the user's custom instructions for one integration.
   */
  getIntegrationInstructions: async (
    integrationId: string,
  ): Promise<IntegrationInstructions> => {
    return await apiService.get<IntegrationInstructions>(
      `/integrations/users/me/integrations/${integrationId}/instructions`,
      { silent: true },
    );
  },

  /**
   * Save the user's custom instructions for one integration.
   */
  updateIntegrationInstructions: async (
    integrationId: string,
    content: string,
  ): Promise<IntegrationInstructions> => {
    return await apiService.put<IntegrationInstructions>(
      `/integrations/users/me/integrations/${integrationId}/instructions`,
      { content },
      { silent: true },
    );
  },

  /**
   * Connect an integration using the unified backend endpoint.
   */
  connectIntegration: async (
    integrationId: string,
    bearerToken?: string,
  ): Promise<{ status: string; name?: string; toolsCount?: number }> => {
    if (typeof window === "undefined")
      return { status: "error", name: "Unknown" };

    const url = new URL(window.location.href);
    url.searchParams.delete("integration");
    url.searchParams.delete("oauth_success");
    url.searchParams.delete("oauth_error");
    const redirectPath = url.pathname + url.search;

    const response = (await apiService.post(
      `/integrations/connect/${integrationId.toLowerCase()}`,
      {
        redirect_path: redirectPath,
        bearer_token: bearerToken,
      },
    )) as {
      status: "connected" | "redirect" | "error";
      integrationId: string;
      name: string;
      message?: string;
      toolsCount?: number;
      redirectUrl?: string;
      error?: string;
    };

    if (response.status === "redirect" && response.redirectUrl) {
      const safeUrl = sanitizeRedirectUrl(response.redirectUrl);
      if (!safeUrl) {
        throw new Error("Invalid redirect URL received from server");
      }
      window.location.href = safeUrl;
      return { status: "redirecting", name: response.name };
    }

    if (response.status === "error") {
      throw new Error(response.error || "Failed to connect integration");
    }

    return {
      status: response.status,
      name: response.name,
      toolsCount: response.toolsCount ?? undefined,
    };
  },

  /**
   * Disconnect an integration. The success toast is fired by the hook caller
   * (useIntegrations.disconnectIntegration) — don't double up here.
   */
  disconnectIntegration: async (integrationId: string): Promise<void> => {
    try {
      await apiService.delete(`/integrations/${integrationId}`);
    } catch (error) {
      console.error(`Failed to disconnect ${integrationId}:`, error);
      throw error;
    }
  },

  /**
   * Create a custom MCP integration.
   */
  createCustomIntegration: async (
    request: CreateCustomIntegrationRequest,
  ): Promise<CreateCustomIntegrationResponse> => {
    try {
      const response = await apiService.post("/integrations/custom", request);
      return response as CreateCustomIntegrationResponse;
    } catch (error) {
      console.error("Failed to create custom integration:", error);
      throw error;
    }
  },

  /**
   * Test connection to an MCP server.
   */
  testConnection: async (
    integrationId: string,
  ): Promise<{
    status: "connected" | "requires_oauth" | "failed";
    tools_count?: number;
    oauth_url?: string;
    error?: string;
  }> => {
    try {
      const response = await apiService.post(`/mcp/test/${integrationId}`, {});
      return response as {
        status: "connected" | "requires_oauth" | "failed";
        tools_count?: number;
        oauth_url?: string;
        error?: string;
      };
    } catch (error) {
      console.error(`Failed to test connection ${integrationId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a custom integration
   */
  deleteCustomIntegration: async (integrationId: string): Promise<void> => {
    try {
      await apiService.delete(`/integrations/custom/${integrationId}`);
    } catch (error) {
      console.error(
        `Failed to delete custom integration ${integrationId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Publish a custom integration to the community marketplace
   */
  publishIntegration: async (
    integrationId: string,
  ): Promise<{
    message: string;
    integrationId: string;
    publicUrl: string;
  }> => {
    const response = await apiService.post(
      `/integrations/custom/${integrationId}/publish`,
      {},
    );
    return response as {
      message: string;
      integrationId: string;
      publicUrl: string;
    };
  },

  /**
   * Unpublish a custom integration from the marketplace
   */
  unpublishIntegration: async (
    integrationId: string,
  ): Promise<{
    message: string;
    integrationId: string;
  }> => {
    const response = await apiService.post(
      `/integrations/custom/${integrationId}/unpublish`,
      {},
    );
    return response as {
      message: string;
      integrationId: string;
    };
  },

  /**
   * Get community integrations for the public marketplace
   */
  getCommunityIntegrations: async (params?: {
    sort?: "popular" | "recent" | "name";
    category?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<CommunityIntegrationsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.sort) searchParams.set("sort", params.sort);
    if (params?.category) searchParams.set("category", params.category);
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.offset) searchParams.set("offset", params.offset.toString());
    if (params?.search) searchParams.set("search", params.search);

    const query = searchParams.toString();
    const response = await apiService.get(
      `/integrations/community${query ? `?${query}` : ""}`,
    );
    return response as CommunityIntegrationsResponse;
  },

  /**
   * Get public integration details by integration ID (no auth required)
   */
  getPublicIntegration: async (
    integrationId: string,
  ): Promise<PublicIntegrationResponse> => {
    const response = await apiService.get(
      `/integrations/public/${integrationId}`,
    );
    return response as PublicIntegrationResponse;
  },

  /**
   * Add a public integration to user's workspace and trigger OAuth if needed
   */
  addIntegration: async (
    integrationId: string,
    bearerToken?: string,
  ): Promise<{
    status:
      | "connected"
      | "redirect"
      | "redirecting"
      | "bearer_required"
      | "error";
    integrationId: string;
    name: string;
    message: string;
    toolsCount?: number;
    redirectUrl?: string;
    error?: string;
  }> => {
    if (typeof window === "undefined") {
      return {
        status: "error",
        integrationId,
        name: "",
        message: "Cannot add integration on server",
      };
    }

    const redirectPath = `/integrations?id=${integrationId}&refresh=true`;

    const response = (await apiService.post(
      `/integrations/public/${integrationId}/add`,
      {
        redirect_path: redirectPath,
        bearer_token: bearerToken,
      },
    )) as {
      status: "connected" | "redirect" | "error";
      integrationId: string;
      name: string;
      message: string;
      toolsCount?: number;
      redirectUrl?: string;
      error?: string;
    };

    if (response.status === "redirect" && response.redirectUrl) {
      const safeUrl = sanitizeRedirectUrl(response.redirectUrl);
      if (!safeUrl) {
        throw new Error("Invalid redirect URL received from server");
      }
      window.location.href = safeUrl;
      return { ...response, status: "redirecting" };
    }

    // Return bearer_required as a special status instead of throwing
    if (response.status === "error" && response.error === "bearer_required") {
      return { ...response, status: "bearer_required" };
    }

    if (response.status === "error") {
      throw new Error(response.error || "Failed to add integration");
    }

    return response;
  },

  /**
   * Get native (platform) integrations for the public marketplace.
   * Fetches from the public /integrations/config endpoint and normalizes
   * to CommunityIntegration shape for card/list compatibility.
   */
  getNativeIntegrations: async (): Promise<CommunityIntegration[]> => {
    const response = await integrationsApi.getIntegrationConfig();
    return response.integrations
      .filter((i) => i.source === "platform" && i.available !== false)
      .sort((a, b) => (b.displayPriority ?? 0) - (a.displayPriority ?? 0))
      .map((i) => ({
        integrationId: i.id,
        slug: i.slug,
        name: i.name,
        description: i.description,
        category: i.category,
        iconUrl: i.iconUrl ?? null,
        cloneCount: 0,
        toolCount: i.tools?.length ?? 0,
        tools: (i.tools ?? []).map((t) => ({
          name: t.name,
          description: t.description ?? null,
        })),
        publishedAt: null,
        creator: null,
        source: "platform" as const,
      }));
  },

  /**
   * Search public integrations using semantic search
   */
  searchIntegrations: async (
    query: string,
  ): Promise<{
    integrations: PublicIntegrationResponse[];
    query: string;
  }> => {
    const response = await apiService.get(
      `/integrations/search?q=${encodeURIComponent(query)}`,
    );
    return response as {
      integrations: PublicIntegrationResponse[];
      query: string;
    };
  },

  /**
   * Get community workflows related to an integration by slug or native ID
   */
  getRelatedWorkflows: async (
    identifier: string,
    limit: number = 10,
  ): Promise<CommunityWorkflowsResponse> => {
    const response = await apiService.get(
      `/integrations/public/${encodeURIComponent(identifier)}/workflows?limit=${limit}`,
      { silent: true },
    );
    return response as CommunityWorkflowsResponse;
  },
};
